import React, { useEffect, useState } from "react";
import { Modal, Table, Empty, Spin, Tag } from "antd";
import { useTranslation } from "react-i18next";
import axios from "../../../services/axiosInstance";
import { Instance } from "types/superAdmin/instanceTypes";
import CustomModal from "components/Common/CustomModal";
import Loader from "components/Common/loader";
import NoDataComponent from "components/Common/NoData";
import Datatable from "components/Common/dataTable";
import { UserBase } from "types/userTypes";
import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";
import { showConfirmDialog } from "utils/confirmDialogUtils";
import useUsers from "../hooks/useUsers";

interface Director {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string | null;
  status?: "active" | "deleted" | string;
  createdAt?: string;

}

interface ShowDirectorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  instance: Instance | null;
  onDelete?: (record: Instance) => void;
}

const ShowDirectorsModal: React.FC<ShowDirectorsModalProps> = ({ isOpen, onClose, instance, onDelete }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [directors, setDirectors] = useState<UserBase[]>([]);
  const { triggerResetPassword } = useUsers();

  useEffect(() => {
    setDirectors(instance?.users?.filter((user) => user.role === "director") || []);
  }, [isOpen, instance?.id]);

    const handleDelete = (record: Instance) => {
      if (!onDelete) {
        console.warn('onDelete function is not provided');
        return;
      }
  
      showConfirmDialog({
        title: t("delete_user"),
        content: t("are_you_sure_you_want_to_delete_this_user"),
        okText: t("yes"),
        cancelText: t("no"),
        okType: "danger",
        loading: false,
        onOk: () => onDelete(record),
      });
    };

  const columns = [
    {
      title: t("last_name"),
      dataIndex: "lastName",
      key: "lastName",
      render: (v: string) => v || "—",
    },
    {
      title: t("first_name"),
      dataIndex: "firstName",
      key: "firstName",
      render: (v: string) => v || "—",
    },
    {
      title: t("email"),
      dataIndex: "email",
      key: "email",
      render: (v: string) => v || "—",
    },
    {
      title: t("phone_number"),
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (v: string) => v || "—",
    },
    {
      title: t("status"),
      dataIndex: "status",
      key: "status",
      render: (v: string) =>
        v ? <Tag color={v === "active" ? "success" : "error"}>{t(v)}</Tag> : "—",
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_: unknown, record: Instance) => {

        const actions: Action[] = [

          {
            label: t("delete"),
            icon: "ti ti-trash",
            actionType: "link",
            onClick: () => handleDelete(record),
          },
          {
            label: t("reset_password_via_email"),
            icon: "ti ti-mail",
            actionType: "modal",
            onClick: async () => {
              await triggerResetPassword({ user_id: record.id, type: "email" })

            },
          },

        ];
        return record.status === "active" ? <ActionsDropdown actions={actions} /> : <i className="ti ti-lock"></i>;
      },
    },

  ];

  return (
    <CustomModal
      title={`${t("directors")} ${instance ? `- ${instance.name}` : ""}`}
      isOpen={isOpen}
      onClose={onClose}

    >
      {loading ? (
        <Loader />
      ) : directors.length ? (
        <Datatable
          columns={columns as any}
          dataSource={directors}
          pagination={false}
        />
      ) : (
        <NoDataComponent />
      )}
    </CustomModal>
  );
};

export default ShowDirectorsModal;
